/**
 * 打ち合わせ（Meet）API クライアント。
 *
 * Slack bot（ai-fassy）・Chrome拡張（meet-calendar）と同じ機構で作成された打ち合わせを、
 * 案件単位で作成・一覧・詳細取得する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 打ち合わせ（作成・一覧・詳細）を読み書きするAPIクライアント
 * @graph-connects /projects/:pjKey/meetings [calls] 打ち合わせ作成・一覧API
 */

import { MEETING_TAGS, type MeetingTag } from '@cortex/domain-project-intake-contract';
import { request } from './_request.js';

/**
 * 打ち合わせの種類タグ（固定リスト）。
 * 定義元は `@cortex/domain-project-intake-contract`（Node専用依存の無い共有パッケージ）。
 * `@cortex/domain-meet` は Firestore 等の Node 専用依存を持ち web から直接 import できないため、
 * バックエンド（`@cortex/domain-meet` 経由）・Web（ここ）の両方が依存の無いこのパッケージから
 * 同じ配列を単一の情報源として import する。
 * @graph-connects none
 */
export { MEETING_TAGS };

/** 打ち合わせの種類タグ */
export type { MeetingTag };

/** 打ち合わせ一覧の1件。 @graph-connects none */
export interface MeetingListItem {
  /** Meet Space ID（spaces/ 接頭辞なし） */
  id: string;
  meetingUri: string;
  meetingCode: string;
  status: 'active' | 'ended';
  /** 作成日時（ISO8601） */
  createdAt: string;
  calendarEventTitle: string | null;
  recordingReady: boolean;
  transcriptReady: boolean;
  /** 議事録(Smart Notes)生成済みフラグ */
  notesReady: boolean;
  /** 録画の元ファイル（Google Drive）を開くURL。未生成または未取得ならnull */
  recordingUrl: string | null;
  /** 文字起こしの元ドキュメント（Google Drive）を開くURL。未生成または未取得ならnull */
  transcriptUrl: string | null;
  /** 議事録(Smart Notes)の元ドキュメント（Google Drive）を開くURL。未生成または未取得ならnull */
  notesUrl: string | null;
  /** 参加者のメールアドレス一覧（Googleカレンダーに招待者が設定されている場合のみ。無ければ空配列） */
  attendeeEmails: string[];
  /** 打ち合わせの目的（作成時に任意入力）。未設定はnull */
  purpose: string | null;
  /** 打ち合わせの種類タグ（Smart Notes取得時にGeminiが自動分類。人手で上書き可） */
  tags: MeetingTag[];
}

/** 打ち合わせ詳細（一覧の要約 + 文字起こし・議事録の本文）。 @graph-connects none */
export interface MeetingDetail extends MeetingListItem {
  transcriptText: string | null;
  transcriptSource: 'gemini_transcript' | 'transcript' | null;
  notesText: string | null;
  /** 議事録が生成されているか（false の場合は notesText も常に null） */
  notesAvailable: boolean;
}

/** 打ち合わせ作成結果。 @graph-connects none */
export interface CreateMeetingResult {
  id: string;
  meetingUri: string;
  meetingCode: string;
}

/** 打ち合わせ作成入力。 @graph-connects none */
export interface CreateMeetingInput {
  /** 打ち合わせの目的（任意） */
  purpose?: string;
}

/** @graph-connects /projects/:pjKey/meetings [calls] 打ち合わせ API */
export const meetingApi = {
  /**
   * 案件の打ち合わせ一覧を作成日時降順で取得する。
   * tags を渡すと、そのいずれかのタグを持つ打ち合わせだけに絞り込む。
   */
  list: (pjKey: string, tags?: MeetingTag[]): Promise<{ meetings: MeetingListItem[] }> => {
    const query = tags && tags.length > 0 ? `?tags=${encodeURIComponent(tags.join(','))}` : '';
    return request(`/projects/${encodeURIComponent(pjKey)}/meetings${query}`);
  },
  /** 案件のSlackチャンネルに紐づく打ち合わせ(Meet)を新規作成する */
  create: (pjKey: string, input: CreateMeetingInput = {}): Promise<CreateMeetingResult> =>
    request(`/projects/${encodeURIComponent(pjKey)}/meetings`, {
      method: 'POST',
      body: JSON.stringify({ purpose: input.purpose }),
    }),
  /** 打ち合わせ1件の文字起こし・議事録を取得する */
  detail: (pjKey: string, meetingId: string): Promise<MeetingDetail> =>
    request(`/projects/${encodeURIComponent(pjKey)}/meetings/${encodeURIComponent(meetingId)}`),
  /** 打ち合わせの種類タグを上書きする（Gemini自動分類の結果を人手で上書きする用途） */
  updateTags: (pjKey: string, meetingId: string, tags: MeetingTag[]): Promise<MeetingListItem> =>
    request(
      `/projects/${encodeURIComponent(pjKey)}/meetings/${encodeURIComponent(meetingId)}/tags`,
      { method: 'PATCH', body: JSON.stringify({ tags }) },
    ),
};
